import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import styled from "styled-components";
import { cn } from "@/lib/utils";
import { CardContent } from "@/components/ui/card";
import { Table, TableHead, TableHeader, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { StyledTableRow } from "@/components/ui/styled-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, Column } from "@/components/ui/data-table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { CandidatesTable } from "./CandidatesTable";
import { TableSortControls } from "@/components/ui/table-sort-controls";
import { AudiencePill, StatusBadge } from "@/components/ui/table-components";
import {
  DesktopContainer,
  MobileContainer,
  PaginationContainer,
  TabCardContainer,
} from "../../sharedStyles";
import CandidateDetailModal from "./CandidateDetailModal";
import ProjectCandidateDetailModal from "./ProjectCandidateDetailModal";
import CandidateDeleteModal from "./CandidateDeleteModal";
import { useQuery, useApolloClient, useMutation } from "@apollo/client";
import {
  GET_CANDIDATES,
  GET_CANDIDATE_BY_ID,
  GET_IMPORTED_CANDIDATES,
} from "../../GraphQL/candidates/queries";
import { IMPORT_CANDIDATE } from "../../GraphQL/candidates/mutations";
import { GET_LOCALITIES } from "../../GraphQL/settings/queries";
import { Candidate, CandidateData, LocalityData } from "../../interfaces";
import { useParams, useNavigate } from "react-router-dom";
import { Download, Edit, Trash2, Plus } from "lucide-react";
import ChevronTableHeader from "../Icons/ChevronTableHeader";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { ProfileContext } from "../../App";
import CandidatesMobileView from "./CandidatesMobileView";
import { CandidatesTableSkeleton } from "./CandidatesTableSkeleton";
import CustomPagination, {
  CustomPaginationType,
} from "../Global/CustomPagination";
import Notification from "../Notification";
import {
  EToastPosition,
  EToastColorStatus,
  IToastData,
} from "../../interfaces";
import config from "../../config";
import guide from "../../assets/role_relay_user_guide.pdf";

const CandidateTitle = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
`;

const Container = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 1rem;
`;


const statusLabels = {
  PLATFORM_INCLUDED: "Pending Opt-in",
  SHARED: "Shared for Collaboration",
  STOPPED_SHARING: "Draft",
  REMOVED: "Removed",
  UNKNOWN: "Unknown",
};

const candidateSortOptions = [
  { value: "firstName", label: "Name" },
  { value: "idealJobTitles", label: "Ideal Job Titles" },
  { value: "industry", label: "Industry" },
  { value: "location.displayName", label: "Location" },
  { value: "yearsOfExperience", label: "Experience" },
  { value: "workType", label: "Type" },
  { value: "audience", label: "Audience" },
  { value: "matches", label: "Matches" },
  { value: "status", label: "Status" },
];

interface Props {
  user: any;
}

const Candidates = ({ user }: Props) => {
  const navigate = useNavigate();
  let atsServer = "au1";
  if (
    user?.username?.includes("firefly") ||
    user?.username?.includes("worktrybe")
  )
    atsServer = "au3";

  if (user?.username?.includes("lloyd") || user?.username?.includes("candidex"))
    atsServer = "au5";

  const profile = useContext(ProfileContext);
  const hasProject =
    profile.userProfile?.company?.settings?.features?.projects || false;
  const showImportedFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showImported
  );
  const myCandidatesPaginationRef = useRef<CustomPaginationType>(null);
  const [nonATS, setNonATS] = useState(profile.connections?.totalCount === 0);

  useEffect(() => {
    setNonATS(profile.connections?.totalCount === 0);
  }, [profile]);

  const [showCandidateDetailModal, setShowCandidateModal] = useState(false);
  const [showProjectCandidateDetailModal, setShowProjectCandidateModal] = useState(false);

  const [modalKey, setModalKey] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(
    null
  );
  const [showCandidateDelete, setShowCandidateDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("candidates");
  const [orderSortInput, setOrderSortInput] = useState<any[]>([
    { updatedAt: "DESC" },
  ]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [toastData, setToastData] = useState<IToastData>({
    show: false,
    color: EToastColorStatus.SUCCESS,
    position: EToastPosition.BOTTOM_END,
    message: "",
  });

  const getCandidateKey = (candidate: Candidate, index: number) => {
    if (candidate?.id != null && candidate.id !== 0) {
      return `candidate-${candidate.id}`;
    }
    if ((candidate as any)?.externalId) {
      return `candidate-ext-${(candidate as any).externalId}`;
    }
    return `candidate-idx-${index}`;
  };

  const { loading, error, data } = useQuery<CandidateData>(GET_CANDIDATES, {
    variables: { skip: 0, take: 25, order: { updatedAt: "DESC" } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const { data: dataLocalities } = useQuery<LocalityData>(GET_LOCALITIES);
  const localities = dataLocalities?.localities?.items
    ? [...dataLocalities?.localities?.items]
    : [];
  const locationName = (x: string): string[] => {
    return localities.map((locality) =>
      x === locality?.name ? `${locality?.name} (${locality?.state})` : ""
    );
  };

  const {
    loading: importedCandidatesLoading,
    error: importedCandidatesError,
    data: importedCandidatesData,
  } = useQuery(GET_IMPORTED_CANDIDATES);



  const [importCandidate] = useMutation(IMPORT_CANDIDATE, {
    onCompleted(dataImported) {
        setActiveTab("candidates");
        const newId = dataImported?.importCandidate?.id;
        navigate(`/my-candidates/${newId}`);
        
        // Pass the skeleton candidate to trigger the modal's fetch
        setSelectedCandidate({ id: newId } as Candidate);
        setShowCandidateModal(true);
      },
    onError(error: any) {
      const errorMessage = error?.networkError?.result?.errors[0]?.message;
      setToastData({
        show: true,
        color: EToastColorStatus.FAILED,
        position: EToastPosition.BOTTOM_END,
        message: `Failed, ${errorMessage}`,
      });
    },
  });



  useEffect(() => {
    setCandidatesData(data?.candidates?.items || []);
    setHasNextPage(data?.candidates?.pageInfo?.hasNextPage || false);
  }, [data]);

  const client = useApolloClient();

  const refreshData = () =>
    client.refetchQueries({
      include: [GET_CANDIDATES],
    });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSortFieldChange = (field: string) => {
    setSortField(field);
    if (!sortField || sortField !== field) {
      setSortDirection('asc');
    }
  };

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const getSortedData = <T extends Record<string, any>>(data: T[]): T[] => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle nested properties like location.displayName
      if (sortField.includes('.')) {
        const [parent, child] = sortField.split('.');
        aVal = a[parent]?.[child];
        bVal = b[parent]?.[child];
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;

      // Numeric comparison for numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const [candidatesData, setCandidatesData] = useState<Candidate[]>(
    data?.candidates?.items || []
  );
  const visibleCandidates = candidatesData.filter(candidate => candidate.lastName?.indexOf('[VMS]') === -1);
  const filteredCandidates = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return visibleCandidates;
    }

    return visibleCandidates.filter((candidate) => {
      const haystack = [
        candidate.firstName,
        candidate.lastName,
        candidate.idealJobTitles,
        candidate.industry,
        candidate.location?.displayName,
        candidate.externalId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [visibleCandidates, searchTerm]);

  const runPagination = (skip = 0, take = 25) => {
    const getCandidatesData = async () => {
      const { data } = await client.query({
        query: GET_CANDIDATES,
        variables: { skip, take, order: { updatedAt: "DESC" } },
      });
      setCandidatesData(data?.candidates?.items);
      myCandidatesPaginationRef.current?.setPagination({
        ...data?.candidates?.pageInfo,
        skip,
        take,
        totalCount: data?.candidates?.totalCount,
      });
    };
    getCandidatesData();
  };

  const handleAddCandidate = () => {
    setModalKey(modalKey + 1);
    setSelectedCandidate(null);
    setShowCandidateModal(true);
  };

  const handleAddProjectCandidate = () => {
    setModalKey(modalKey + 1);
    setSelectedCandidate(null);
    setShowProjectCandidateModal(true);
  };

  /**
   * Logic for deeplinked candidates
   */
  const [routeCandidateId, setRouteCandidateId] = useState<number | null>(null);
  const { candidateId } = useParams<{
    candidateId: string;
  }>();

  useEffect(() => {
    candidateId
      ? setRouteCandidateId(Number.parseInt(candidateId))
      : setRouteCandidateId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  if (routeCandidateId !== null && candidatesData.length > 0 && !showCandidateDetailModal) {
    const candidate = candidatesData.find((c) => c.id === routeCandidateId);
    if (candidate) {
      setNonATS(false);
      setSelectedCandidate(candidate);
      setShowCandidateModal(true);
    }
  }
}, [routeCandidateId, candidatesData]);

  useEffect(() => {
    runPagination();
  }, [data?.candidates?.items]);

  useEffect(() => {
    setCandidatesData(candidatesData);
  }, [candidatesData]);

  const handleShowModalDelete = (id: number) => {
    setShowCandidateDelete(true);
    setDeleteId(id);
  };

  const handleCandidateDetails = (data: Candidate) => {
    navigate(`/my-candidates/${data?.id}`);
    setNonATS(false);
    setSelectedCandidate(data);
    setShowCandidateModal(true);
  };

  const handleProjectCandidateDetails = (data: Candidate) => {
    navigate(`/my-candidates/${data?.id}`);
    setNonATS(false);
    setSelectedCandidate(data);
    setShowProjectCandidateModal(true);
  };

  const handleImportCandidateModal = (data: Candidate) => {
    const externalId = data?.externalId;
    const candidateId = externalId.replace(/\D/g, "");
    importCandidate({
      variables: {
        candidateId: Number(candidateId),
      },
    });
  };

  const onHandleActiveTab = (activeTab: string | null) => {
    if (activeTab) {
      setActiveTab(activeTab);
    }
  };

  const handleShowToast = (
    color: EToastColorStatus,
    position: EToastPosition,
    message: string
  ) => {
    setToastData({ show: true, color, position, message });
  };

  const importedCandidatesColumns = useMemo<Column<Candidate>[]>(() => [
    {
      id: "firstName",
      header: "Name",
      cell: (candidate) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {candidate?.firstName} {candidate?.lastName?.slice(0, 1)}.
        </span>
      ),
    },
    {
      id: "idealJobTitles",
      header: "Job Title",
      cell: (candidate) => (
        <span className="text-slate-700 dark:text-slate-300">{candidate?.idealJobTitles || "-"}</span>
      ),
    },
    {
      id: "industry",
      header: "Industry",
      cell: (candidate) => (
        <span className="text-slate-700 dark:text-slate-300">{candidate?.industry || "-"}</span>
      ),
    },
    {
      id: "location.displayName",
      header: "Location",
      cell: (candidate) => (
        <span className="text-slate-700 dark:text-slate-300">
          {locationName(candidate?.location?.displayName as string) ?? "-"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      className: "text-center",
      cell: (candidate) => <StatusBadge status={statusLabels[candidate?.status] || 'Unknown'} />,
    },
    {
      id: "actions",
      header: "Actions",
      className: "text-center",
      width: "120px",
      cell: (candidate) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleImportCandidateModal(candidate);
            }}
            className="hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/30 dark:hover:text-teal-300"
          >
            <Download size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleShowModalDelete(candidate?.id);
            }}
            className="hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ),
    },
  ], [locationName]);

  return (
    <Container>
      <CandidateDetailModal
        key={modalKey}
        isNonATS={nonATS}
        show={showCandidateDetailModal}
        onHide={() => {
          window.history.replaceState(null, "", `/my-candidates/`);
          setRouteCandidateId(null);
          setShowCandidateModal(false);
        }}
        candidate={selectedCandidate}
        reloadCandidates={refreshData}
        atsServer={atsServer}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
        onShowDelete={(data: boolean) => setShowCandidateDelete(data)}
        onSetDeleteId={(data: number) => setDeleteId(data)}
      />
      <ProjectCandidateDetailModal
        key={modalKey}
        isNonATS={true}
        show={showProjectCandidateDetailModal}
        onHide={() => {
          window.history.replaceState(null, "", `/my-candidates/`);
          setRouteCandidateId(null);
          setShowProjectCandidateModal(false);
        }}
        candidate={selectedCandidate}
        reloadCandidates={refreshData}
        atsServer={atsServer}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
        onShowDelete={(data: boolean) => setShowCandidateDelete(data)}
        onSetDeleteId={(data: number) => setDeleteId(data)}
      />
      <CandidateDeleteModal
        id={deleteId}
        show={showCandidateDelete}
        onHideModal={() => setShowCandidateDelete(false)}
        reloadCandidates={refreshData}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
      />
      <PageHeader title="My Candidates" subtitle="Manage your talent pool" />
      <Tabs value={activeTab} onValueChange={(value) => onHandleActiveTab(value)}>
        <TabsList>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
          {showImportedFlag && <TabsTrigger value="imported-candidates">Imported Candidates</TabsTrigger>}
          {hasProject && <TabsTrigger value="project_candidates">Project Candidates</TabsTrigger>}
        </TabsList>
        <TabsContent value="candidates">
          <div className="space-y-4">
            {/* Header row with search/sort and action button */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <TableSortControls
                sortField={sortField || "firstName"}
                sortDirection={sortDirection}
                options={candidateSortOptions}
                onSortFieldChange={handleSortFieldChange}
                onSortDirectionToggle={toggleSortDirection}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by name, title, industry, location..."
              />
              <Button
                onClick={handleAddCandidate}
                className={cn(
                  "h-10 px-5 shrink-0",
                  "bg-primary hover:bg-primary/90",
                  "text-primary-foreground",
                  "shadow-[0_2px_8px_rgba(10,95,111,0.25)]",
                  "hover:shadow-[0_4px_12px_rgba(10,95,111,0.35)]",
                  "transition-all duration-200 ease-out",
                  "active:scale-[0.97] active:shadow-[0_1px_4px_rgba(10,95,111,0.2)]",
                  "font-medium"
                )}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Candidate
              </Button>
            </div>

            {loading && (
              <DesktopContainer>
                <CandidatesTableSkeleton rows={5} />
              </DesktopContainer>
            )}

            {!loading && error && (
              <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
                <p className="text-rose-700 dark:text-rose-300">
                  Unable to load candidates at this time - please try again soon
                </p>
              </div>
            )}

            {/* Desktop Table */}
            {!loading && (
              <DesktopContainer>
                <CandidatesTable
                  candidates={getSortedData(filteredCandidates)}
                  locationName={locationName}
                  onCandidateClick={handleCandidateDetails}
                  onEditCandidate={handleCandidateDetails}
                  onDeleteCandidate={handleShowModalDelete}
                  sortBy={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  isLoading={false}
                />
              </DesktopContainer>
            )}

            {/* Mobile View */}
            {!loading && (
              <MobileContainer>
                <CandidatesMobileView
                  onShowCandidateModal={() => setShowCandidateModal(true)}
                  onSelectCandidate={(data) => setSelectedCandidate(data)}
                  onSortingCandidatesData={(data) => handleSort(data)}
                  candidatesData={getSortedData(filteredCandidates)}
                  locationName={locationName}
                  handleShowModalDelete={handleShowModalDelete}
                />
              </MobileContainer>
            )}

            {/* Pagination */}
            {hasNextPage && (
              <div className="flex justify-end">
                <CustomPagination
                  ref={myCandidatesPaginationRef}
                  onPaginationChange={(args) =>
                    runPagination(args.skip, args.take)
                  }
                />
              </div>
            )}
          </div>
        </TabsContent>
        {showImportedFlag && (
          <TabsContent value="imported-candidates">
            <div className="space-y-4">
              {importedCandidatesError && (
                <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
                  <p className="text-rose-700 dark:text-rose-300">
                    Unable to load imported candidates at this time - please try again soon
                  </p>
                </div>
              )}
              <DataTable
                data={importedCandidatesData?.importedCandidates?.items || []}
                columns={importedCandidatesColumns}
                getRowId={getCandidateKey}
                isLoading={importedCandidatesLoading}
                emptyMessage="No imported candidates yet."
              />
            </div>
          </TabsContent>
        )}
        {hasProject && (
          <TabsContent value="project_candidates">
            <TabCardContainer>
            <CardContent>
              {loading && <div style={{ marginBottom: "8px" }}>Loading...</div>}
              {!loading && error && (
                <div style={{ marginBottom: "8px" }}>
                  Unable to load candidates at this time - please try again soon
                </div>
              )}
              <DesktopContainer>
                <Table hidden={loading}>
                  <TableHeader>
                    <TableRow>
                      <TableHead onClick={() => handleSort("firstName")}>
                        <CandidateTitle>
                          <div>Name</div>
                          <div>
                            {sortField === "firstName" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead onClick={() => handleSort("idealJobTitles")}>
                        <CandidateTitle>
                          <div>Job Title</div>
                          <div>
                            {sortField === "idealJobTitles" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead onClick={() => handleSort("industry")}>
                        <CandidateTitle>
                          <div>Industry</div>
                          <div>
                            {sortField === "industry" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead onClick={() => handleSort("location.displayName")}>
                        <CandidateTitle>
                          <div>Location</div>
                          <div>
                            {sortField === "location.displayName" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead onClick={() => handleSort("yearsOfExperience")}>
                        <CandidateTitle>
                          <div>Exp. (yrs)</div>
                          <div>
                            {sortField === "yearsOfExperience" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead
                        className="text-center"
                        onClick={() => handleSort("workType")}
                      >
                        <CandidateTitle>
                          <div>Type</div>
                          <div>
                            {sortField === "workType" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead
                        className="text-center"
                        onClick={() => handleSort("audience")}
                      >
                        <CandidateTitle>
                          <div>Audience</div>
                          <div>
                            {sortField === "audience" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead
                        className="text-center"
                        onClick={() => handleSort("matches")}
                      >
                        <CandidateTitle>
                          <div>Matches</div>
                          <div>
                            {sortField === "matches" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead
                        className="text-center"
                        onClick={() => handleSort("status")}
                      >
                        <CandidateTitle>
                          <div>Status</div>
                          <div>
                            {sortField === "status" && (
                              <ChevronTableHeader
                                ascending={sortDirection === "asc"}
                              />
                            )}
                          </div>
                        </CandidateTitle>
                      </TableHead>
                      <TableHead className="text-center" style={{ minWidth: "85px" }}>
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedData(candidatesData.filter(candidate => candidate.lastName && candidate.lastName.indexOf('[VMS]') > -1)).map((candidate, index) => (
                      <StyledTableRow key={getCandidateKey(candidate, index)}>
                        <TableCell onClick={() => handleProjectCandidateDetails(candidate)}>
                          {candidate?.firstName}{" "}
                          {candidate?.lastName?.replace('[VMS] ','').slice(0, 1)}.
                        </TableCell>
                        <TableCell onClick={() => handleProjectCandidateDetails(candidate)}>
                          {candidate?.idealJobTitles || "-"}
                        </TableCell>
                        <TableCell onClick={() => handleProjectCandidateDetails(candidate)}>
                          {candidate?.industry || "-"}
                        </TableCell>
                        <TableCell onClick={() => handleProjectCandidateDetails(candidate)}>
                          {locationName(
                            candidate?.location?.displayName as string
                          )}
                        </TableCell>
                        <TableCell onClick={() => handleProjectCandidateDetails(candidate)}>
                          {candidate?.yearsOfExperience || "-"}
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleProjectCandidateDetails(candidate)}
                        >
                          <Badge variant="secondary">
                            {candidate.workType
                              ? candidate.workType === "TEMP"
                                ? "CONTRACT/TEMP"
                                : candidate.workType
                              : "-"}
                          </Badge>
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleProjectCandidateDetails(candidate)}
                        >
                          <AudiencePill audience={candidate.audience || 'PRIVATE'} />
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleProjectCandidateDetails(candidate)}
                        >
                          <Badge
                            variant={candidate.matches ? "default" : "secondary"}
                          >
                            {candidate.matches}
                          </Badge>{" "}
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleProjectCandidateDetails(candidate)}
                        >
                          <StatusBadge status={statusLabels[candidate.status] || 'Unknown'} />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleProjectCandidateDetails(candidate)}
                          >
                            <Edit size={16} />
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleShowModalDelete(candidate?.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </DesktopContainer>
              <MobileContainer>
                <CandidatesMobileView
                  onShowCandidateModal={() => setShowCandidateModal(true)}
                  onSelectCandidate={(data) => setSelectedCandidate(data)}
                  onSortingCandidatesData={(data) => handleSort(data)}
                  onSetNonATS={() => setNonATS(false)}
                  candidatesData={candidatesData.filter(candidate => candidate.lastName && candidate.lastName.indexOf('[VMS]') > -1)}
                  locationName={locationName}
                  handleShowModalDelete={handleShowModalDelete}
                />
              </MobileContainer>
              {!loading && !candidatesData.length && (
                <p>
                  You have no candidates included at this time. Keep an eye on
                  your inbox as we will send you an email when we detect
                  potential roles for your candidates.
                </p>
              )}
              <PaginationContainer>
                <p>
                  <Button onClick={handleAddProjectCandidate}>Add Project Candidate</Button>
                </p>
                {hasNextPage && (
                  <CustomPagination
                    ref={myCandidatesPaginationRef}
                    onPaginationChange={(args) =>
                      runPagination(args.skip, args.take)
                    }
                  />
                )}
              </PaginationContainer>
              <p>
                <a href={guide} target="_blank" rel="noreferrer">
                  Click here to view the User Guide.
                </a>
              </p>
            </CardContent>
            </TabCardContainer>
          </TabsContent>
        )}
      </Tabs>
      <Notification
        delay={3000}
        toastData={toastData}
        onClose={() => setToastData({ ...toastData, show: false })}
      />
    </Container>
  );
};

export default Candidates;
